import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  DocumentArrowDownIcon,
  MagnifyingGlassCircleIcon,
  NoSymbolIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import SectionHeader from '../../components/UI/SectionHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import ConfirmationDialog from '../../components/UI/ConfirmationDialog';
import {
  duplicateLeadsService,
  DuplicateActionLogSummary,
  DuplicateBulkAction,
  DuplicateCluster,
  DuplicateClusterAction,
  DuplicateLeadScan,
  DuplicateResolutionLog,
  DuplicateScanMode,
  ScanCampaignOption,
} from '../../services/duplicateLeadsService';

const PAGE_SIZE = 25;

interface CampaignGroup {
  campaignId: string;
  campaignName: string;
  members: DuplicateCluster['members'];
}

function groupMembersByCampaign(cluster: DuplicateCluster): CampaignGroup[] {
  const map = new Map<string, CampaignGroup>();
  for (const member of cluster.members) {
    const existing = map.get(member.campaignId);
    if (existing) {
      existing.members.push(member);
    } else {
      map.set(member.campaignId, {
        campaignId: member.campaignId,
        campaignName: member.campaignName,
        members: [member],
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.campaignName.localeCompare(b.campaignName));
}

function getOldestLeadId(cluster: DuplicateCluster): string | undefined {
  const sorted = [...cluster.members].sort((a, b) => {
    const aTime = a.leadCreatedAt ? new Date(a.leadCreatedAt).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.leadCreatedAt ? new Date(b.leadCreatedAt).getTime() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });
  return sorted[0]?.leadId;
}

function getDefaultTargetCampaigns(cluster: DuplicateCluster, keeperLeadId?: string): string[] {
  const groups = groupMembersByCampaign(cluster);
  const keeperCampaignId = cluster.members.find((m) => m.leadId === keeperLeadId)?.campaignId;
  if (groups.length <= 1) return [];
  return groups
    .filter((g) => g.campaignId !== keeperCampaignId)
    .map((g) => g.campaignId);
}

function getScanFoundTotals(
  scan: DuplicateLeadScan | null,
  bulkActions: DuplicateBulkAction[],
  actionLogSummary: DuplicateActionLogSummary | null
): { foundGroups: number; foundLeads: number } {
  const completedBulk = bulkActions.find((b) => b.status === 'completed' && b.totalClusters > 0);
  const foundGroups =
    scan?.initialTotalClusters ||
    completedBulk?.totalClusters ||
    bulkActions[0]?.totalClusters ||
    scan?.totalClusters ||
    (actionLogSummary && actionLogSummary.totalLogEntries > 0 ? actionLogSummary.totalLeadsAffected : 0) ||
    0;
  const foundLeads =
    scan?.initialTotalDuplicateLeads ||
    completedBulk?.affectedLeads ||
    actionLogSummary?.totalLeadsAffected ||
    scan?.totalDuplicateLeads ||
    0;
  return { foundGroups, foundLeads };
}

function hasResolutionHistory(
  actionLogSummary: DuplicateActionLogSummary | null,
  bulkActions: DuplicateBulkAction[]
): boolean {
  if (actionLogSummary && actionLogSummary.totalLogEntries > 0) return true;
  return bulkActions.some((b) => b.status === 'completed' && b.affectedLeads > 0);
}

function formatActionLabel(action: DuplicateClusterAction): string {
  if (action === 'keep_one') return 'Kept one lead, removed copies';
  if (action === 'mark_dnc') return 'Marked as Do Not Call';
  return 'Deleted leads';
}

function isBulkSummaryLog(log: DuplicateResolutionLog): boolean {
  return !log.keeperLeadId && log.keeperFullName.includes('Bulk action summary');
}

function formatScanElapsed(startedAt: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return `${hours}h ${remMins}m`;
  }
  return `${mins}m ${secs}s`;
}

function isScanStepOneStale(scan: DuplicateLeadScan): boolean {
  if (scan.progressPercent >= 5) return false;
  const startedMs = scan.startedAt ? new Date(scan.startedAt).getTime() : 0;
  const updatedMs = new Date(scan.updatedAt).getTime();
  const now = Date.now();
  return startedMs > 0 && now - startedMs > 20 * 60 * 1000 && now - updatedMs > 3 * 60 * 1000;
}

function formatScanModeLabel(mode: DuplicateScanMode | undefined): string {
  if (mode === 'cross_campaign') return 'Cross-campaign only';
  if (mode === 'same_campaign') return 'Same campaign only';
  return 'Cross-campaign + same campaign';
}

function formatScanDuration(startedAt: string | null, finishedAt: string | null): string | null {
  if (!startedAt || !finishedAt) return null;
  const seconds = Math.max(1, Math.round((new Date(finishedAt).getTime() - new Date(startedAt).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const remSecs = seconds % 60;
  return `${mins}m ${remSecs}s`;
}

const DuplicateLeadsPage: React.FC = () => {
  const [scans, setScans] = useState<DuplicateLeadScan[]>([]);
  const [activeScan, setActiveScan] = useState<DuplicateLeadScan | null>(null);
  const [clusters, setClusters] = useState<DuplicateCluster[]>([]);
  const [expandedClusterId, setExpandedClusterId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [startingScan, setStartingScan] = useState(false);
  const [scanMode, setScanMode] = useState<DuplicateScanMode>('both');
  const [cancellingScan, setCancellingScan] = useState(false);
  const [scanCampaigns, setScanCampaigns] = useState<ScanCampaignOption[]>([]);
  const [activeBulkAction, setActiveBulkAction] = useState<DuplicateBulkAction | null>(null);
  const [bulkTargetCampaignIds, setBulkTargetCampaignIds] = useState<string[]>([]);
  const [bulkMode, setBulkMode] = useState<'oldest' | 'campaigns'>('oldest');
  const [showBulkCampaignPicker, setShowBulkCampaignPicker] = useState(false);
  const [showClusterAdvanced, setShowClusterAdvanced] = useState<Record<string, boolean>>({});
  const [bulkStarting, setBulkStarting] = useState(false);
  const [bulkCancelling, setBulkCancelling] = useState(false);
  const bulkStreamRef = useRef<EventSource | null>(null);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClusters, setTotalClusters] = useState(0);
  const [pageTab, setPageTab] = useState<'groups' | 'logs'>('groups');
  const [actionLogSummary, setActionLogSummary] = useState<DuplicateActionLogSummary | null>(null);
  const [actionLogs, setActionLogs] = useState<DuplicateResolutionLog[]>([]);
  const [bulkActionsHistory, setBulkActionsHistory] = useState<DuplicateBulkAction[]>([]);
  const [loadingActionLogs, setLoadingActionLogs] = useState(false);
  const [, setScanClock] = useState(0);
  const [actionLogPage, setActionLogPage] = useState(1);
  const [actionLogTotalPages, setActionLogTotalPages] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [keeperByCluster, setKeeperByCluster] = useState<Record<string, string>>({});
  const [targetCampaignsByCluster, setTargetCampaignsByCluster] = useState<Record<string, string[]>>({});
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    message: string;
    variant: 'warning' | 'destructive';
    confirmText: string;
    onConfirm: () => void;
  } | null>(null);
  const scanStreamRef = useRef<EventSource | null>(null);
  const currentPageRef = useRef(1);
  const clustersLoadedForScanRef = useRef<string | null>(null);

  currentPageRef.current = currentPage;

  const getKeeperForCluster = (cluster: DuplicateCluster) =>
    keeperByCluster[cluster.id] || getOldestLeadId(cluster) || cluster.members[0]?.leadId;

  const openCluster = (cluster: DuplicateCluster) => {
    const opening = expandedClusterId !== cluster.id;
    setExpandedClusterId(opening ? cluster.id : null);
    if (opening && !keeperByCluster[cluster.id]) {
      const oldestId = getOldestLeadId(cluster);
      if (oldestId) {
        setKeeperByCluster((prev) => ({ ...prev, [cluster.id]: oldestId }));
        setTargetCampaignsByCluster((prev) => ({
          ...prev,
          [cluster.id]: getDefaultTargetCampaigns(cluster, oldestId),
        }));
      }
    }
  };

  const selectKeeper = (cluster: DuplicateCluster, leadId: string) => {
    setKeeperByCluster((prev) => ({ ...prev, [cluster.id]: leadId }));
    setTargetCampaignsByCluster((prev) => ({
      ...prev,
      [cluster.id]: getDefaultTargetCampaigns(cluster, leadId),
    }));
  };

  const getTargetCampaignsForCluster = (cluster: DuplicateCluster) => {
    if (targetCampaignsByCluster[cluster.id]) {
      return targetCampaignsByCluster[cluster.id];
    }
    return getDefaultTargetCampaigns(cluster, getKeeperForCluster(cluster));
  };

  const toggleTargetCampaign = (clusterId: string, campaignId: string, cluster: DuplicateCluster) => {
    const current = getTargetCampaignsForCluster(cluster);
    const next = current.includes(campaignId)
      ? current.filter((id) => id !== campaignId)
      : [...current, campaignId];
    setTargetCampaignsByCluster((prev) => ({ ...prev, [clusterId]: next }));
  };

  const getActionPreview = (cluster: DuplicateCluster, action: DuplicateClusterAction) => {
    const targetCampaignIds = getTargetCampaignsForCluster(cluster);
    const keeperLeadId = getKeeperForCluster(cluster);
    const targetedMembers = cluster.members.filter((m) => targetCampaignIds.includes(m.campaignId));
    const leadCount =
      action === 'keep_one'
        ? targetedMembers.filter((m) => m.leadId !== keeperLeadId).length
        : targetedMembers.length;

    const groups = groupMembersByCampaign(cluster).filter((g) => targetCampaignIds.includes(g.campaignId));

    return {
      leadCount,
      campaignNames: groups.map((g) => `${g.campaignName} (${g.members.length})`),
    };
  };

  const loadScans = useCallback(async () => {
    const list = await duplicateLeadsService.listScans();
    setScans(list);
    setActiveScan((current) => current ?? list[0] ?? null);
  }, []);

  const loadClusters = useCallback(async (scanId: string, page = 1) => {
    setLoadingClusters(true);
    try {
      const result = await duplicateLeadsService.getClusters(scanId, page, PAGE_SIZE);
      setClusters(result.clusters);
      const parsedTotalPages = parseInt(result.pagination.totalPages, 10) || 0;
      const parsedTotal = parseInt(result.pagination.total, 10) || 0;
      setTotalPages(parsedTotalPages > 0 ? parsedTotalPages : 1);
      setTotalClusters(parsedTotal);
      setCurrentPage(page);
      const updatedScan = await duplicateLeadsService.getScan(scanId);
      setActiveScan(updatedScan);
      setScans((prev) => prev.map((s) => (s.id === updatedScan.id ? updatedScan : s)));
    } catch {
      toast.error('Failed to load duplicate groups');
    } finally {
      setLoadingClusters(false);
    }
  }, []);

  const resetScanViewState = useCallback(() => {
    setClusters([]);
    setTotalClusters(0);
    setTotalPages(1);
    setCurrentPage(1);
    setExpandedClusterId(null);
    setKeeperByCluster({});
    setTargetCampaignsByCluster({});
    setActiveBulkAction(null);
    setBulkActionsHistory([]);
    setActionLogs([]);
    setActionLogSummary(null);
    setActionLogPage(1);
    setExpandedLogId(null);
    clustersLoadedForScanRef.current = null;
  }, []);

  const applyScanUpdate = useCallback((updated: DuplicateLeadScan) => {
    setActiveScan(updated);
    setScans((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    if (updated.status === 'completed' && clustersLoadedForScanRef.current !== updated.id) {
      clustersLoadedForScanRef.current = updated.id;
      setCurrentPage(1);
      loadClusters(updated.id, 1);
    }
  }, [loadClusters]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadScans();
      } catch (err: any) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          toast.error('Duplicate Leads API not found. Please restart the backend server.');
        } else {
          toast.error('Failed to load duplicate scans');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadScans]);

  useEffect(() => {
    if (!activeScan || activeScan.status !== 'completed') return;
    if (clustersLoadedForScanRef.current === activeScan.id) return;
    clustersLoadedForScanRef.current = activeScan.id;
    loadClusters(activeScan.id, 1);
  }, [activeScan?.id, activeScan?.status, loadClusters]);

  useEffect(() => {
    if (!activeScan) return;
    const isRunning = activeScan.status === 'queued' || activeScan.status === 'running';
    if (!isRunning || typeof window === 'undefined' || !('EventSource' in window)) {
      scanStreamRef.current?.close();
      scanStreamRef.current = null;
      return;
    }

    const streamUrl = duplicateLeadsService.getScanStreamUrl(activeScan.id);
    const es = new EventSource(streamUrl);
    scanStreamRef.current = es;

    const handleScanPayload = (raw: string) => {
      try {
        const payload = JSON.parse(raw) as DuplicateLeadScan;
        applyScanUpdate(payload);
      } catch {
        // ignore malformed events
      }
    };

    es.addEventListener('duplicate-scan', (event) => {
      handleScanPayload((event as MessageEvent).data);
    });

    es.onerror = () => {
      es.close();
      scanStreamRef.current = null;
    };

    const pollInterval = setInterval(async () => {
      if (!activeScan) return;
      try {
        const latest = await duplicateLeadsService.getScan(activeScan.id);
        applyScanUpdate(latest);
        if (latest.status === 'completed' || latest.status === 'failed' || latest.status === 'cancelled') {
          clearInterval(pollInterval);
        }
      } catch {
        // ignore transient poll errors
      }
    }, 12_000);

    return () => {
      clearInterval(pollInterval);
      es.close();
      scanStreamRef.current = null;
    };
  }, [activeScan?.id, activeScan?.status, applyScanUpdate]);

  useEffect(() => {
    const running = activeScan?.status === 'queued' || activeScan?.status === 'running';
    if (!running) return;
    const timer = setInterval(() => setScanClock((c) => c + 1), 1000);
    return () => clearInterval(timer);
  }, [activeScan?.id, activeScan?.status]);

  const loadScanCampaigns = useCallback(async (scanId: string) => {
    try {
      const campaigns = await duplicateLeadsService.getScanCampaigns(scanId);
      setScanCampaigns(campaigns);
    } catch {
      setScanCampaigns([]);
    }
  }, []);

  const loadBulkActions = useCallback(async (scanId: string) => {
    try {
      const list = await duplicateLeadsService.listBulkActions(scanId, 20);
      setBulkActionsHistory(list);
      const running = list.find((b) => b.status === 'queued' || b.status === 'running');
      setActiveBulkAction(running ?? null);
    } catch {
      setActiveBulkAction(null);
      setBulkActionsHistory([]);
    }
  }, []);

  const loadActionLogsData = useCallback(async (scanId: string, page = 1) => {
    setLoadingActionLogs(true);
    try {
      const [summary, logsResult, bulkList] = await Promise.all([
        duplicateLeadsService.getActionLogSummary(scanId),
        duplicateLeadsService.getActionLogs(scanId, page, PAGE_SIZE),
        duplicateLeadsService.listBulkActions(scanId, 20),
      ]);
      setActionLogSummary(summary);
      setActionLogs(logsResult.logs);
      setActionLogTotalPages(parseInt(logsResult.pagination.totalPages, 10) || 1);
      setBulkActionsHistory(bulkList);
      setActionLogPage(page);
    } catch {
      toast.error('Failed to load action logs');
      setActionLogSummary(null);
      setActionLogs([]);
      setBulkActionsHistory([]);
    } finally {
      setLoadingActionLogs(false);
    }
  }, []);

  useEffect(() => {
    if (!activeScan || activeScan.status !== 'completed') {
      setScanCampaigns([]);
      setActiveBulkAction(null);
      setActionLogSummary(null);
      return;
    }
    loadScanCampaigns(activeScan.id);
    loadBulkActions(activeScan.id);
    duplicateLeadsService
      .getActionLogSummary(activeScan.id)
      .then(setActionLogSummary)
      .catch(() => setActionLogSummary(null));
  }, [activeScan?.id, activeScan?.status, loadScanCampaigns, loadBulkActions]);

  useEffect(() => {
    if (pageTab !== 'logs' || !activeScan || activeScan.status !== 'completed') return;
    loadActionLogsData(activeScan.id, actionLogPage);
  }, [pageTab, activeScan?.id, activeScan?.status, actionLogPage, loadActionLogsData]);

  useEffect(() => {
    if (!activeBulkAction) return;
    const isRunning = activeBulkAction.status === 'queued' || activeBulkAction.status === 'running';
    if (!isRunning || typeof window === 'undefined' || !('EventSource' in window)) {
      bulkStreamRef.current?.close();
      bulkStreamRef.current = null;
      return;
    }

    const streamUrl = duplicateLeadsService.getBulkStreamUrl(activeBulkAction.id);
    const es = new EventSource(streamUrl);
    bulkStreamRef.current = es;

    es.addEventListener('duplicate-bulk', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent).data) as DuplicateBulkAction;
        setActiveBulkAction(payload);
        if (payload.status === 'completed' && activeScan) {
          loadClusters(activeScan.id, currentPageRef.current);
          loadActionLogsData(activeScan.id, 1);
        }
      } catch {
        // ignore
      }
    });

    es.onerror = () => {
      es.close();
      bulkStreamRef.current = null;
    };

    const pollInterval = setInterval(async () => {
      if (!activeBulkAction) return;
      try {
        const list = await duplicateLeadsService.listBulkActions(activeScan?.id || '', 5);
        const latest = list.find((b) => b.id === activeBulkAction.id);
        if (latest) {
          setActiveBulkAction(latest);
          if (latest.status === 'completed' && activeScan) {
            loadClusters(activeScan.id, 1);
            loadActionLogsData(activeScan.id, 1);
            loadBulkActions(activeScan.id);
            clearInterval(pollInterval);
          } else if (latest.status === 'failed' || latest.status === 'cancelled') {
            clearInterval(pollInterval);
          }
        }
      } catch {
        // ignore
      }
    }, 10_000);

    return () => {
      clearInterval(pollInterval);
      es.close();
      bulkStreamRef.current = null;
    };
  }, [activeBulkAction?.id, activeBulkAction?.status, activeScan, loadClusters, loadActionLogsData, loadBulkActions]);

  const handleCancelScan = async () => {
    if (!activeScan) return;
    setCancellingScan(true);
    try {
      const scan = await duplicateLeadsService.cancelScan(activeScan.id);
      applyScanUpdate(scan);
      toast.success('Scan cancelled. You can start a new scan.');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to cancel scan');
    } finally {
      setCancellingScan(false);
    }
  };

  const handleStartScan = async () => {
    setStartingScan(true);
    try {
      const scan = await duplicateLeadsService.startScan(undefined, scanMode);
      resetScanViewState();
      setCurrentPage(1);
      setActiveScan(scan);
      setScans((prev) => [scan, ...prev.filter((s) => s.id !== scan.id)]);
      toast.success('Scan started in background. Server load is minimized — results will appear when ready.');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to start scan');
    } finally {
      setStartingScan(false);
    }
  };

  const getActionWarnings = (cluster: DuplicateCluster, action: DuplicateClusterAction) => {
    const targetCampaignIds = getTargetCampaignsForCluster(cluster);
    const keeperLeadId = getKeeperForCluster(cluster);
    const targetedMembers = cluster.members.filter((m) => targetCampaignIds.includes(m.campaignId));
    const affectedMembers =
      action === 'keep_one'
        ? targetedMembers.filter((m) => m.leadId !== keeperLeadId)
        : targetedMembers;

    const activeCampaigns = [
      ...new Map(
        affectedMembers
          .filter((m) => m.campaignIsActive)
          .map((m) => [m.campaignId, m.campaignName])
      ).values(),
    ];
    const liveCallLeads = affectedMembers.filter((m) => m.isOnLiveCall);

    return { activeCampaigns, liveCallLeads, hasWarning: activeCampaigns.length > 0 || liveCallLeads.length > 0 };
  };

  const executeAction = async (
    cluster: DuplicateCluster,
    action: DuplicateClusterAction,
    keeperLeadId?: string,
    targetCampaignIds?: string[]
  ) => {
    if (!activeScan) return;

    setActionLoading(cluster.id);
    try {
      const actionOptions: { keeperLeadId?: string; targetCampaignIds?: string[] } = {
        targetCampaignIds,
      };
      if (action === 'keep_one' && keeperLeadId) {
        actionOptions.keeperLeadId = keeperLeadId;
      }
      const result = await duplicateLeadsService.applyClusterAction(
        activeScan.id,
        cluster.id,
        action,
        actionOptions
      );

      if (result.affected === 0) {
        toast.error('No leads were updated — they may already be removed.');
      } else {
        const names = result.affectedCampaigns.map((c) => c.campaignName).join(', ');
        if (result.liveCallLeadIds.length > 0) {
          toast.success(
            `Updated ${result.affected} lead(s) in: ${names} (included ${result.liveCallLeadIds.length} on live call)`
          );
        } else {
          toast.success(`Updated ${result.affected} lead(s) successfully`);
        }
      }

      setExpandedClusterId(null);
      const updatedScan = await duplicateLeadsService.getScan(activeScan.id);
      applyScanUpdate(updatedScan);
      const reloadPage = currentPage;
      await loadClusters(activeScan.id, reloadPage);
      duplicateLeadsService
        .getActionLogSummary(activeScan.id)
        .then(setActionLogSummary)
        .catch(() => undefined);
      if (pageTab === 'logs') {
        await loadActionLogsData(activeScan.id, actionLogPage);
      }
      await loadBulkActions(activeScan.id);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(null);
      setConfirmDialog(null);
    }
  };

  const handleAction = (cluster: DuplicateCluster, action: DuplicateClusterAction) => {
    if (!activeScan) return;

    const keeperLeadId = getKeeperForCluster(cluster);
    const targetCampaignIds = getTargetCampaignsForCluster(cluster);

    if (action === 'keep_one' && !keeperLeadId) {
      toast.error('Select a lead to keep');
      return;
    }
    if (targetCampaignIds.length === 0) {
      toast.error('Select at least one campaign to apply action on');
      return;
    }

    const preview = getActionPreview(cluster, action);
    const campaignList = preview.campaignNames.join(', ');
    const warnings = getActionWarnings(cluster, action);

    const actionLabel =
      action === 'keep_one' ? 'remove duplicates' : action === 'mark_dnc' ? 'mark as Do Not Call' : 'delete leads';

    let message =
      action === 'keep_one'
        ? `Keep "${cluster.members.find((m) => m.leadId === keeperLeadId)?.fullName || 'selected lead'}" and ${actionLabel} for ${preview.leadCount} other lead(s).`
        : action === 'mark_dnc'
          ? `Mark ${preview.leadCount} lead(s) as Do Not Call.`
          : `Soft-delete ${preview.leadCount} lead(s).`;

    if (warnings.hasWarning) {
      const warningLines: string[] = [];
      if (warnings.activeCampaigns.length > 0) {
        warningLines.push(`⚠ Active campaigns: ${warnings.activeCampaigns.join(', ')}`);
      }
      if (warnings.liveCallLeads.length > 0) {
        warningLines.push(
          `⚠ Live calls in progress: ${warnings.liveCallLeads.map((m) => `${m.fullName} (${m.campaignName})`).join(', ')}`
        );
      }
      message = `${warningLines.join('\n')}\n\nThese campaigns are currently running. Proceeding may interrupt live calls.\n\n${message}\n\nDo you still want to continue?`;
    } else {
      message = `${message}\n\nDo you want to continue?`;
    }

    setConfirmDialog({
      title: warnings.hasWarning ? 'Active Campaign Warning' : 'Confirm Action',
      message,
      variant: warnings.hasWarning ? 'warning' : action === 'mark_dnc' ? 'warning' : 'destructive',
      confirmText: warnings.hasWarning ? 'Yes, proceed anyway' : 'Confirm',
      onConfirm: () => executeAction(cluster, action, keeperLeadId, targetCampaignIds),
    });
  };

  const handleExport = async () => {
    if (!activeScan || activeScan.status !== 'completed') return;
    try {
      await duplicateLeadsService.exportScan(activeScan.id);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const handleExportActionLogs = async () => {
    if (!activeScan || activeScan.status !== 'completed') return;
    try {
      await duplicateLeadsService.exportActionLogs(activeScan.id);
      toast.success('Action logs export downloaded');
    } catch {
      toast.error('Action logs export failed');
    }
  };

  const toggleBulkTargetCampaign = (campaignId: string) => {
    setBulkTargetCampaignIds((prev) =>
      prev.includes(campaignId) ? prev.filter((id) => id !== campaignId) : [...prev, campaignId]
    );
  };

  const runBulkAction = (action: DuplicateClusterAction) => {
    if (!activeScan) return;

    const isOldest = bulkMode === 'oldest';
    if (!isOldest && bulkTargetCampaignIds.length === 0) {
      toast.error('Select at least one campaign');
      return;
    }

    const scopeLabel = isOldest
      ? `all ${activeScan.totalClusters.toLocaleString()} clusters (keep oldest lead in each)`
      : `all clusters — only leads in ${bulkTargetCampaignIds.length} selected campaign(s)`;

    const actionLabel =
      action === 'delete'
        ? isOldest
          ? 'delete duplicate leads (keep oldest in each cluster)'
          : 'delete leads from selected campaigns'
        : isOldest
          ? 'mark duplicates as Do Not Call (keep oldest in each cluster)'
          : 'mark leads as Do Not Call in selected campaigns';

    setConfirmDialog({
      title: 'Confirm bulk action',
      message: `This will ${actionLabel} across ${scopeLabel}.\n\nThe job runs in the background. You can track progress on this page.\n\nContinue?`,
      variant: action === 'delete' ? 'destructive' : 'warning',
      confirmText: 'Yes, start bulk action',
      onConfirm: () =>
        handleStartBulkAction(action, {
          ...(isOldest ? { keeperStrategy: 'oldest_lead' as const } : { requireCampaigns: true }),
        }),
    });
  };

  const handleStartBulkAction = async (
    action: DuplicateClusterAction,
    options: {
      keeperStrategy?: 'oldest_lead' | 'newest_lead';
      requireCampaigns?: boolean;
    }
  ) => {
    if (!activeScan) return;
    if (options.requireCampaigns && bulkTargetCampaignIds.length === 0) {
      toast.error('Select at least one target campaign');
      return;
    }

    setBulkStarting(true);
    try {
      const bulk = await duplicateLeadsService.startBulkAction(activeScan.id, {
        action,
        scope: 'all',
        ...(options.keeperStrategy ? { keeperStrategy: options.keeperStrategy } : {}),
        ...(bulkTargetCampaignIds.length > 0 ? { targetCampaignIds: bulkTargetCampaignIds } : {}),
      });
      setActiveBulkAction(bulk);
      setConfirmDialog(null);
      toast.success(`Bulk action started for ${activeScan.totalClusters.toLocaleString()} clusters`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to start bulk action');
    } finally {
      setBulkStarting(false);
    }
  };

  const handleCancelBulkAction = async () => {
    if (!activeBulkAction) return;
    setBulkCancelling(true);
    try {
      const bulk = await duplicateLeadsService.cancelBulkAction(activeBulkAction.id);
      setActiveBulkAction(bulk);
      toast.success('Bulk action cancelled');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to cancel bulk action');
    } finally {
      setBulkCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const isScanRunning = activeScan?.status === 'queued' || activeScan?.status === 'running';
  const isBulkRunning =
    activeBulkAction?.status === 'queued' || activeBulkAction?.status === 'running';
  const { foundGroups, foundLeads } = getScanFoundTotals(
    activeScan,
    bulkActionsHistory,
    actionLogSummary
  );
  const remainingClusters = loadingClusters
    ? (activeScan?.totalClusters ?? totalClusters)
    : totalClusters;
  const resolvedByHistory = hasResolutionHistory(actionLogSummary, bulkActionsHistory);
  const isFullyResolved =
    activeScan?.status === 'completed' &&
    !loadingClusters &&
    remainingClusters === 0 &&
    (foundGroups > 0 || resolvedByHistory);
  const isCleanScan =
    activeScan?.status === 'completed' &&
    !loadingClusters &&
    remainingClusters === 0 &&
    foundGroups === 0 &&
    !resolvedByHistory;
  const scanDurationLabel = formatScanDuration(
    activeScan?.startedAt ?? null,
    activeScan?.finishedAt ?? null
  );
  const completedBulkAction =
    bulkActionsHistory.find((b) => b.status === 'completed') ?? null;
  const showBulkResolveCard =
    remainingClusters > 0 && !isFullyResolved && !loadingClusters;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Duplicate Leads"
        description="Find duplicate phone numbers across campaigns or inside the same campaign (primary + alternate phones). Scans run in the background to avoid server overload."
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <MagnifyingGlassCircleIcon className="h-5 w-5" />
            Duplicate Scan
          </CardTitle>
          <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
            {activeScan?.status === 'completed' && (
              <Button variant="outlined" size="sm" onClick={handleExport}>
                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                Export CSV
              </Button>
            )}
            {isScanRunning && activeScan && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancelScan}
                loading={cancellingScan}
                disabled={cancellingScan}
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Cancel Scan
              </Button>
            )}
            <Button
              onClick={handleStartScan}
              loading={startingScan}
              disabled={isScanRunning || startingScan}
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              {isScanRunning ? 'Scan Running...' : 'Run Scan'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isScanRunning && (
            <div className="space-y-2">
              <p className="text-sm font-medium">What to scan for</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <label className="flex items-start gap-2 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/30 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <input
                    type="radio"
                    name="scan-mode"
                    className="mt-1"
                    checked={scanMode === 'both'}
                    onChange={() => setScanMode('both')}
                    disabled={startingScan}
                  />
                  <div>
                    <p className="text-sm font-medium">Both (recommended)</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Cross-campaign and same-campaign duplicates.
                    </p>
                  </div>
                </label>
                <label className="flex items-start gap-2 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/30 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <input
                    type="radio"
                    name="scan-mode"
                    className="mt-1"
                    checked={scanMode === 'cross_campaign'}
                    onChange={() => setScanMode('cross_campaign')}
                    disabled={startingScan}
                  />
                  <div>
                    <p className="text-sm font-medium">Cross-campaign only</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Same phone in 2+ different campaigns.
                    </p>
                  </div>
                </label>
                <label className="flex items-start gap-2 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/30 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                  <input
                    type="radio"
                    name="scan-mode"
                    className="mt-1"
                    checked={scanMode === 'same_campaign'}
                    onChange={() => setScanMode('same_campaign')}
                    disabled={startingScan}
                  />
                  <div>
                    <p className="text-sm font-medium">Same campaign only</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Same phone repeated inside one campaign.
                    </p>
                  </div>
                </label>
              </div>
              {scanMode !== 'cross_campaign' && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Same-campaign scans can be large (hundreds of thousands of groups) and may take longer.
                </p>
              )}
            </div>
          )}

          {activeScan?.scanMode && (
            <p className="text-xs text-muted-foreground">
              Active scan mode: <strong>{formatScanModeLabel(activeScan.scanMode)}</strong>
            </p>
          )}

          {isScanRunning && activeScan && (
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <span className="text-sm font-medium">Scan in progress — server safe mode</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">{activeScan.progressPercent}%</span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleCancelScan}
                    loading={cancellingScan}
                    disabled={cancellingScan}
                  >
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    Cancel Scan
                  </Button>
                </div>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${activeScan.progressPercent}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {activeScan.progressPercent < 5 && (
                  <>
                    <span className="block font-medium text-foreground">
                      Step 1/4: Finding duplicate phone numbers across all campaigns…
                    </span>
                    {activeScan.startedAt && (
                      <span className="block mt-1">Elapsed: {formatScanElapsed(activeScan.startedAt)}</span>
                    )}
                    <span className="block mt-1">
                      Progress stays at 2% until this database step finishes. On large datasets this often takes
                      15–30 minutes — it is usually not stuck.
                    </span>
                    {isScanStepOneStale(activeScan) && (
                      <span className="block mt-1 text-destructive">
                        No server heartbeat for 3+ minutes after 20 minutes elapsed. The background worker may have
                        stopped — cancel this scan, restart the queue worker, then run a new scan.
                      </span>
                    )}
                  </>
                )}
                {activeScan.totalPhonesScanned > 0 && (
                  <span className="block">
                    Phones processed: {activeScan.totalPhonesScanned.toLocaleString()}
                  </span>
                )}
                {activeScan.progressPercent >= 5 && activeScan.progressPercent < 85 && (
                  <span className="block">Step 2/4: Processing duplicate phones in batches…</span>
                )}
                Progress updates via live stream. Scan runs in background queue.
                {activeScan.progressPercent >= 85 && activeScan.progressPercent < 90 && (
                  <span className="block mt-1 text-warning">
                    Building duplicate clusters — large datasets may take several minutes. Progress should move past 85% within a few minutes after backend restart.
                  </span>
                )}
                {activeScan.progressPercent >= 90 && activeScan.progressPercent < 100 && (
                  <span className="block mt-1">Saving results to database…</span>
                )}
                {activeScan.progressPercent >= 85 && (
                  <span className="block mt-1">
                    Stuck here for 10+ minutes? Cancel and run a new scan after restarting the backend.
                  </span>
                )}
              </p>
            </div>
          )}

          {activeScan?.status === 'failed' && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Scan failed: {activeScan.errorMessage || 'Unknown error'}
            </div>
          )}

          {activeScan?.status === 'completed' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg border p-4 space-y-1">
                <p className="text-sm font-medium">Duplicate Phones</p>
                <p className="text-2xl font-semibold">{activeScan.totalPhonesScanned.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Duplicate phone numbers found by this scan (cross-campaign and/or same-campaign, based on scan mode).
                </p>
              </div>
              <div className="rounded-lg border p-4 space-y-1">
                <p className="text-sm font-medium">Duplicate Groups</p>
                {isFullyResolved ? (
                  <>
                    <p className="text-2xl font-semibold">0 remaining</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Originally found <strong>{foundGroups.toLocaleString()}</strong> groups across campaigns.
                      All have been resolved for this scan.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-semibold">{activeScan.totalClusters.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      A group is leads sharing the same phone — either across campaigns or inside one campaign.
                    </p>
                  </>
                )}
              </div>
              <div className="rounded-lg border p-4 space-y-1">
                <p className="text-sm font-medium">Affected Leads</p>
                {isFullyResolved ? (
                  <>
                    <p className="text-2xl font-semibold">
                      {(completedBulkAction?.affectedLeads ?? foundLeads).toLocaleString()} updated
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Duplicate copies were removed or marked DNC. One lead per group was kept.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-semibold">{activeScan.totalDuplicateLeads.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Total leads inside all duplicate groups. One lead is counted once per group it belongs to.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {isCleanScan && (
            <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 p-4 text-sm space-y-1">
              <p className="font-medium text-sky-800 dark:text-sky-300">
                Scan completed — no duplicates found
              </p>
              <p className="text-muted-foreground">
                The scan ran successfully
                {scanDurationLabel ? ` in ${scanDurationLabel}` : ''} using{' '}
                <strong>{formatScanModeLabel(activeScan?.scanMode)}</strong>. No matching duplicate groups were found.
                {activeScan?.totalPhonesScanned === 0 && (
                  <>
                    {' '}
                    This usually means your earlier bulk cleanup worked, or new imports have not re-introduced
                    cross-campaign duplicates.
                  </>
                )}
                {' '}
                Try a different scan mode if you expected duplicates in another category.
              </p>
            </div>
          )}

          {isFullyResolved && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm space-y-1">
              <p className="font-medium text-emerald-700 dark:text-emerald-300">Scan complete — all duplicates resolved</p>
              <p className="text-muted-foreground">
                This scan originally found <strong>{foundGroups.toLocaleString()}</strong> duplicate groups.
                {completedBulkAction && (
                  <>
                    {' '}
                    Bulk action updated <strong>{completedBulkAction.affectedLeads.toLocaleString()}</strong> leads
                    ({completedBulkAction.processedClusters.toLocaleString()} groups processed).
                  </>
                )}
                {' '}The group list is empty because duplicates were already cleaned up — not because data was lost.
                Run a <strong>new scan</strong> only if you imported new leads and want to check again.
              </p>
            </div>
          )}

          {scans.length > 1 && (
            <div>
              <label className="text-sm font-medium">Previous scans</label>
              <select
                className="mt-1 w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={activeScan?.id || ''}
                onChange={(e) => {
                  const scan = scans.find((s) => s.id === e.target.value) || null;
                  resetScanViewState();
                  setActiveScan(scan);
                  setPageTab('groups');
                  if (scan?.status === 'completed') {
                    loadClusters(scan.id, 1);
                    loadBulkActions(scan.id);
                    duplicateLeadsService
                      .getActionLogSummary(scan.id)
                      .then(setActionLogSummary)
                      .catch(() => setActionLogSummary(null));
                  }
                }}
              >
                {scans.map((scan) => {
                  const scanFound = getScanFoundTotals(scan, bulkActionsHistory, null);
                  const scanLabel =
                    scan.totalClusters > 0
                      ? `${scan.totalClusters} remaining`
                      : scanFound.foundGroups > 0
                        ? `${scanFound.foundGroups} found (resolved)`
                        : '0 groups';
                  return (
                  <option key={scan.id} value={scan.id}>
                    {new Date(scan.createdAt).toLocaleString()} — {scan.status} ({scanLabel})
                  </option>
                  );
                })}
              </select>
            </div>
          )}
        </CardContent>
      </Card>

      {activeScan?.status === 'completed' && (
        <div className="border-b border-border">
          <nav className="-mb-px flex space-x-8" aria-label="Duplicate leads tabs">
            <button
              type="button"
              onClick={() => setPageTab('groups')}
              className={clsx(
                pageTab === 'groups'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground',
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors'
              )}
            >
              Duplicate Groups
              <span
                className={clsx(
                  pageTab === 'groups' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                  'ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium'
                )}
              >
                {remainingClusters.toLocaleString()}
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setPageTab('logs');
                setActionLogPage(1);
              }}
              className={clsx(
                pageTab === 'logs'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground',
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors'
              )}
            >
              <span className="inline-flex items-center gap-1.5">
                <ClipboardDocumentListIcon className="h-4 w-4" />
                Action Logs
              </span>
              {actionLogSummary && (
                <span
                  className={clsx(
                    pageTab === 'logs' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                    'ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium'
                  )}
                >
                  {actionLogSummary.totalLogEntries.toLocaleString()}
                </span>
              )}
            </button>
          </nav>
        </div>
      )}

      {activeScan?.status === 'completed' && pageTab === 'groups' && showBulkResolveCard && (
        <Card>
          <CardHeader>
            <CardTitle>Resolve All Duplicates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-muted-foreground">
              Apply one action to all <strong>{totalClusters.toLocaleString()}</strong> duplicate groups at once.
              Runs in the background — safe for large datasets.
            </p>

            {isBulkRunning && activeBulkAction && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="text-sm font-medium">Processing…</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">{activeBulkAction.progressPercent}%</span>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleCancelBulkAction}
                      loading={bulkCancelling}
                      disabled={bulkCancelling}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${activeBulkAction.progressPercent}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {activeBulkAction.processedClusters.toLocaleString()} / {activeBulkAction.totalClusters.toLocaleString()} groups
                  · {activeBulkAction.affectedLeads.toLocaleString()} leads updated
                </p>
              </div>
            )}

            {activeBulkAction?.status === 'failed' && !isBulkRunning && !isFullyResolved && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                Last action failed: {activeBulkAction.errorMessage || 'Unknown error'}
              </div>
            )}

            {!isBulkRunning && (
              <>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/30 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <input
                      type="radio"
                      name="bulk-mode"
                      className="mt-1"
                      checked={bulkMode === 'oldest'}
                      onChange={() => setBulkMode('oldest')}
                    />
                    <div>
                      <p className="text-sm font-medium">Recommended: keep oldest lead in each group</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        In every duplicate group, the earliest lead stays. All other copies are removed or marked DNC.
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/30 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <input
                      type="radio"
                      name="bulk-mode"
                      className="mt-1"
                      checked={bulkMode === 'campaigns'}
                      onChange={() => {
                        setBulkMode('campaigns');
                        setShowBulkCampaignPicker(true);
                      }}
                    />
                    <div>
                      <p className="text-sm font-medium">Only affect specific campaigns</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Choose which campaigns to clean up. At least one campaign per group stays untouched.
                      </p>
                    </div>
                  </label>
                </div>

                {bulkMode === 'campaigns' && (
                  <div className="rounded-lg border border-border p-3 space-y-2">
                    <button
                      type="button"
                      className="text-sm font-medium text-primary hover:underline"
                      onClick={() => setShowBulkCampaignPicker((v) => !v)}
                    >
                      {showBulkCampaignPicker ? 'Hide' : 'Show'} campaign picker
                      {bulkTargetCampaignIds.length > 0 && ` (${bulkTargetCampaignIds.length} selected)`}
                    </button>
                    {showBulkCampaignPicker && scanCampaigns.length > 0 && (
                      <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto pt-1">
                        {scanCampaigns.map((c) => (
                          <label
                            key={c.campaignId}
                            className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1 text-xs cursor-pointer ${
                              bulkTargetCampaignIds.includes(c.campaignId)
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:bg-muted/40'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={bulkTargetCampaignIds.includes(c.campaignId)}
                              onChange={() => toggleBulkTargetCampaign(c.campaignId)}
                            />
                            {c.campaignName}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-3 pt-1">
                  <Button
                    variant="destructive"
                    disabled={bulkStarting || (bulkMode === 'campaigns' && bulkTargetCampaignIds.length === 0)}
                    loading={bulkStarting}
                    onClick={() => runBulkAction('delete')}
                  >
                    <TrashIcon className="h-4 w-4 mr-1.5" />
                    Delete duplicates
                  </Button>
                  <Button
                    variant="warning"
                    disabled={bulkStarting || (bulkMode === 'campaigns' && bulkTargetCampaignIds.length === 0)}
                    onClick={() => runBulkAction('mark_dnc')}
                  >
                    <NoSymbolIcon className="h-4 w-4 mr-1.5" />
                    Mark as Do Not Call
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {activeScan?.status === 'completed' && pageTab === 'groups' && (
        <Card>
          <CardHeader>
            <CardTitle>
              Duplicate Groups
              {isFullyResolved
                ? ` (${foundGroups.toLocaleString()} found — all resolved)`
                : ` (${remainingClusters.toLocaleString()})`}
            </CardTitle>
            <p className="text-sm text-muted-foreground font-normal mt-1">
              {isFullyResolved
                ? 'Individual groups are no longer listed because duplicates were already removed from campaigns.'
                : 'Expand a group to review leads and fix one group at a time.'}
            </p>
          </CardHeader>
          <CardContent>
            {loadingClusters ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : clusters.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                {isFullyResolved
                  ? `All ${Math.max(foundGroups, 1).toLocaleString()} duplicate groups from this scan have been resolved. Kept leads remain in their campaigns. Run a new scan to check for newly imported duplicates.`
                  : isCleanScan
                    ? `Scan finished with zero duplicate groups for ${formatScanModeLabel(activeScan?.scanMode).toLowerCase()}.`
                  : remainingClusters === 0 && !loadingClusters
                    ? 'No duplicate groups found in this scan.'
                    : totalClusters === 0 && loadingClusters
                      ? 'Loading duplicate groups…'
                    : 'No duplicate groups on this page. Try another page or refresh the list.'}
              </p>
            ) : (
              <div className="space-y-3">
                {clusters.map((cluster) => {
                  const isExpanded = expandedClusterId === cluster.id;
                  const keeperId = getKeeperForCluster(cluster);
                  const keeperMember = cluster.members.find((m) => m.leadId === keeperId);
                  const deletePreview = getActionPreview(cluster, 'keep_one');
                  const dncPreview = getActionPreview(cluster, 'mark_dnc');
                  const showAdvanced = showClusterAdvanced[cluster.id];

                  return (
                    <div key={cluster.id} className="rounded-lg border border-border overflow-hidden">
                      <button
                        type="button"
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/40"
                        onClick={() => openCluster(cluster)}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4 shrink-0" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4 shrink-0" />
                          )}
                          <span className="font-medium">
                            {cluster.leadCount} leads · {cluster.campaignCount} campaign
                            {cluster.campaignCount === 1 ? '' : 's'}
                          </span>
                          <span
                            className={clsx(
                              'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded',
                              cluster.campaignCount === 1
                                ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                                : 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
                            )}
                          >
                            {cluster.campaignCount === 1 ? 'Same campaign' : 'Cross-campaign'}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground truncate max-w-[45%] ml-2 font-mono">
                          {cluster.sharedPhones[0]}
                          {cluster.sharedPhones.length > 1 && ` +${cluster.sharedPhones.length - 1}`}
                        </span>
                      </button>

                      {isExpanded && (
                        <div className="border-t border-border px-4 py-4 space-y-4 bg-muted/10">
                          <div>
                            <p className="text-sm font-medium mb-2">Step 1 — Choose the lead to keep</p>
                            <div className="grid gap-2 sm:grid-cols-2">
                              {cluster.members.map((member) => {
                                const isKeeper = keeperId === member.leadId;
                                const isOldest = getOldestLeadId(cluster) === member.leadId;
                                return (
                                  <button
                                    key={member.leadId}
                                    type="button"
                                    onClick={() => selectKeeper(cluster, member.leadId)}
                                    className={`rounded-lg border p-3 text-left text-sm transition-colors ${
                                      isKeeper
                                        ? 'border-primary bg-primary/10 ring-1 ring-primary'
                                        : 'border-border hover:bg-muted/40'
                                    }`}
                                  >
                                    <p className="font-medium truncate">{member.fullName || 'Unknown'}</p>
                                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                                      {member.campaignName}
                                    </p>
                                    <p className="text-xs font-mono text-muted-foreground mt-0.5">{member.phoneE164}</p>
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs capitalize">
                                        {member.status.replace(/_/g, ' ')}
                                      </span>
                                      {isOldest && (
                                        <span className="inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary">
                                          Oldest
                                        </span>
                                      )}
                                      {isKeeper && (
                                        <span className="inline-flex items-center rounded-full bg-green-500/15 px-2 py-0.5 text-xs text-green-600 dark:text-green-400">
                                          Keeping
                                        </span>
                                      )}
                                      {member.isOnLiveCall && (
                                        <span className="inline-flex items-center rounded-full bg-warning/15 px-2 py-0.5 text-xs text-warning">
                                          Live call
                                        </span>
                                      )}
                                      {member.campaignIsActive && (
                                        <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                          Active campaign
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div className="rounded-lg border border-border bg-background p-3 text-sm">
                            <p className="font-medium">Step 2 — Apply to this group</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Keeping <strong>{keeperMember?.fullName || 'selected lead'}</strong> in{' '}
                              <strong>{keeperMember?.campaignName}</strong>.
                              {deletePreview.leadCount > 0 ? (
                                <>
                                  {' '}
                                  Will affect <strong>{deletePreview.leadCount}</strong> other lead(s) in{' '}
                                  {deletePreview.campaignNames.length} campaign(s).
                                </>
                              ) : (
                                ' Adjust campaigns below if needed.'
                              )}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button
                                size="sm"
                                variant="destructive"
                                loading={actionLoading === cluster.id}
                                disabled={deletePreview.leadCount === 0}
                                onClick={() => handleAction(cluster, 'keep_one')}
                              >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Remove {deletePreview.leadCount} duplicate(s)
                              </Button>
                              <Button
                                size="sm"
                                variant="warning"
                                loading={actionLoading === cluster.id}
                                disabled={dncPreview.leadCount === 0}
                                onClick={() => handleAction(cluster, 'mark_dnc')}
                              >
                                <NoSymbolIcon className="h-4 w-4 mr-1" />
                                Mark {dncPreview.leadCount} as DNC
                              </Button>
                            </div>
                          </div>

                          <div>
                            <button
                              type="button"
                              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                              onClick={() =>
                                setShowClusterAdvanced((prev) => ({
                                  ...prev,
                                  [cluster.id]: !prev[cluster.id],
                                }))
                              }
                            >
                              {showAdvanced ? (
                                <ChevronDownIcon className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRightIcon className="h-3.5 w-3.5" />
                              )}
                              Advanced: choose which campaigns to affect
                            </button>
                            {showAdvanced && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {groupMembersByCampaign(cluster).map((group) => {
                                  const selected = getTargetCampaignsForCluster(cluster).includes(group.campaignId);
                                  const isKeeperCampaign = keeperMember?.campaignId === group.campaignId;
                                  return (
                                    <label
                                      key={group.campaignId}
                                      className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs cursor-pointer ${
                                        selected
                                          ? 'border-destructive/50 bg-destructive/10'
                                          : 'border-border opacity-60'
                                      } ${isKeeperCampaign ? 'opacity-40 cursor-not-allowed' : ''}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        disabled={isKeeperCampaign}
                                        onChange={() =>
                                          toggleTargetCampaign(cluster.id, group.campaignId, cluster)
                                        }
                                      />
                                      {group.campaignName} ({group.members.length})
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4">
                    <Button
                      variant="outlined"
                      size="sm"
                      disabled={currentPage <= 1}
                      onClick={() => activeScan && loadClusters(activeScan.id, currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outlined"
                      size="sm"
                      disabled={currentPage >= totalPages}
                      onClick={() => activeScan && loadClusters(activeScan.id, currentPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeScan?.status === 'completed' && pageTab === 'logs' && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle>Action Logs</CardTitle>
              <p className="text-sm text-muted-foreground font-normal mt-1">
                Record of kept leads and removed duplicates for this scan. Per-group detail is available for actions
                run after this update.
              </p>
            </div>
            <Button variant="outlined" size="sm" onClick={handleExportActionLogs}>
              <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
              Export Logs CSV
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {loadingActionLogs ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {actionLogSummary && (
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="rounded-lg border p-4">
                      <p className="text-xs text-muted-foreground">Log entries</p>
                      <p className="text-xl font-semibold">{actionLogSummary.totalLogEntries.toLocaleString()}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-xs text-muted-foreground">Leads kept</p>
                      <p className="text-xl font-semibold">{actionLogSummary.totalLeadsKept.toLocaleString()}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-xs text-muted-foreground">Leads removed / updated</p>
                      <p className="text-xl font-semibold">{actionLogSummary.totalLeadsAffected.toLocaleString()}</p>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-xs text-muted-foreground">Bulk action runs</p>
                      <p className="text-xl font-semibold">{actionLogSummary.bulkActionCount.toLocaleString()}</p>
                    </div>
                  </div>
                )}

                {bulkActionsHistory.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium">Bulk action runs</h3>
                    <div className="space-y-2">
                      {bulkActionsHistory.map((bulk) => (
                        <div key={bulk.id} className="rounded-lg border border-border px-4 py-3 text-sm">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-medium">{formatActionLabel(bulk.action)}</span>
                            <span className="text-xs uppercase tracking-wide text-muted-foreground">{bulk.status}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(bulk.createdAt).toLocaleString()} ·{' '}
                            {bulk.processedClusters.toLocaleString()} / {bulk.totalClusters.toLocaleString()} groups ·{' '}
                            {bulk.affectedLeads.toLocaleString()} leads updated
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <h3 className="text-sm font-medium">Detailed log entries</h3>
                  {actionLogs.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">
                      No action logs yet for this scan. Logs are created when you remove duplicates or mark leads as DNC.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {actionLogs.map((log) => {
                        const isExpanded = expandedLogId === log.id;
                        const summary = isBulkSummaryLog(log);
                        return (
                          <div key={log.id} className="rounded-lg border border-border overflow-hidden">
                            <button
                              type="button"
                              className="w-full px-4 py-3 text-left hover:bg-muted/40"
                              onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium">
                                    {summary
                                      ? `Bulk summary — ${log.affectedCount.toLocaleString()} leads updated`
                                      : log.keeperFullName
                                        ? `Kept: ${log.keeperFullName}`
                                        : formatActionLabel(log.action)}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {new Date(log.createdAt).toLocaleString()} · {formatActionLabel(log.action)} ·{' '}
                                    {log.source === 'bulk' ? 'Bulk' : 'Single group'}
                                    {!summary && log.affectedCount > 0 && (
                                      <> · {log.affectedCount} removed</>
                                    )}
                                  </p>
                                </div>
                                {!summary && log.sharedPhones[0] && (
                                  <span className="text-xs font-mono text-muted-foreground truncate max-w-[40%]">
                                    {log.sharedPhones[0]}
                                  </span>
                                )}
                              </div>
                            </button>

                            {isExpanded && (
                              <div className="border-t border-border px-4 py-3 space-y-3 bg-muted/20 text-sm">
                                {summary ? (
                                  <p className="text-muted-foreground">
                                    <span className="inline-block rounded bg-muted px-1.5 py-0.5 text-xs font-medium mr-1">
                                      Summary only
                                    </span>
                                    This bulk run completed before per-group logging was enabled.
                                    {log.affectedCount.toLocaleString()} leads were updated across{' '}
                                    {bulkActionsHistory.find((b) => b.id === log.bulkActionId)?.totalClusters.toLocaleString() ??
                                      'many'}{' '}
                                    groups.
                                  </p>
                                ) : (
                                  <>
                                    {log.keeperLeadId && (
                                      <div>
                                        <p className="text-xs font-medium text-muted-foreground mb-1">Kept lead</p>
                                        <p>
                                          {log.keeperFullName || '—'} · {log.keeperCampaignName || '—'} ·{' '}
                                          {log.keeperPhoneE164 || '—'}
                                        </p>
                                      </div>
                                    )}
                                    {log.affectedLeads.length > 0 && (
                                      <div>
                                        <p className="text-xs font-medium text-muted-foreground mb-1">
                                          Removed / updated ({log.affectedLeads.length})
                                        </p>
                                        <ul className="space-y-1">
                                          {log.affectedLeads.map((lead) => (
                                            <li key={lead.leadId} className="text-xs">
                                              {lead.fullName || '—'} · {lead.campaignName} · {lead.phoneE164}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {actionLogTotalPages > 1 && (
                    <div className="flex items-center justify-between pt-2">
                      <Button
                        variant="outlined"
                        size="sm"
                        disabled={actionLogPage <= 1}
                        onClick={() => setActionLogPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {actionLogPage} of {actionLogTotalPages}
                      </span>
                      <Button
                        variant="outlined"
                        size="sm"
                        disabled={actionLogPage >= actionLogTotalPages}
                        onClick={() => setActionLogPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmationDialog
        isOpen={confirmDialog !== null}
        onClose={() => setConfirmDialog(null)}
        onConfirm={() => confirmDialog?.onConfirm()}
        title={confirmDialog?.title || 'Confirm Action'}
        message={confirmDialog?.message || ''}
        confirmText={confirmDialog?.confirmText || 'Confirm'}
        cancelText="No, cancel"
        isLoading={actionLoading !== null || bulkStarting}
        variant={confirmDialog?.variant || 'destructive'}
      />
    </div>
  );
};

export default DuplicateLeadsPage;
